import React, { useState, useEffect } from 'react';
import { Star, User, Calendar, Image as ImageIcon, ShieldCheck } from 'lucide-react';
import { getTranslation as t, isRTL } from '../utils/translations';
import { getProductReviews, getProductReviewStats, submitReview } from '../api/client';

const ProductReviews = ({ productId, storeSettings }) => {
    const [reviews, setReviews] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    
    // Form state
    const [showForm, setShowForm] = useState(false);
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState(false);

    useEffect(() => {
        if (!productId) return;
        fetchData();
    }, [productId, page]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [statsRes, reviewsRes] = await Promise.all([
                getProductReviewStats(productId),
                getProductReviews(productId, page, 5)
            ]);
            setStats(statsRes);
            setReviews(reviewsRes?.data || []);
            setTotalPages(reviewsRes?.totalPages || 1);
        } catch (error) {
            console.error('Failed to fetch reviews', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (rating < 1 || rating > 5) return;
        setSubmitting(true);
        setSubmitError('');
        try {
            await submitReview(productId, { rating, comment });
            setSubmitSuccess(true);
            setShowForm(false);
            setRating(5);
            setComment('');
            // Refresh data
            setPage(0);
            fetchData();
        } catch (error) {
            console.error('Failed to submit review', error);
            setSubmitError(error?.response?.data?.message || 'Failed to submit review. You must be logged in and have purchased this product.');
        } finally {
            setSubmitting(false);
        }
    };

    const renderStars = (ratingValue, size = 16) => {
        return (
            <div style={{ display: 'flex', gap: '2px', color: '#111' }}>
                {[1, 2, 3, 4, 5].map((star) => (
                    <Star 
                        key={star} 
                        size={size} 
                        fill={star <= ratingValue ? '#111' : 'transparent'} 
                        strokeWidth={1.5}
                    />
                ))}
            </div>
        );
    };

    if (loading && !stats && reviews.length === 0) {
        return <div style={{ padding: '40px 0', textAlign: 'center' }}>Loading reviews...</div>;
    }

    return (
        <section className="product-reviews-section" style={{ marginTop: '40px', paddingTop: '40px', borderTop: '1px solid #EAEAEA' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
                <h2 style={{ fontSize: '24px', fontWeight: '700', margin: 0, color: '#111' }}>{t('CLIENT_REVIEWS') || 'Client Reviews'}</h2>
                <button 
                    onClick={() => setShowForm(!showForm)}
                    style={{ 
                        padding: '12px 24px', 
                        background: '#111', 
                        color: '#fff', 
                        borderRadius: '12px', 
                        fontWeight: '600', 
                        border: 'none', 
                        cursor: 'pointer',
                        fontSize: '14px'
                    }}
                >
                    {showForm ? 'Cancel' : 'Write a Review'}
                </button>
            </div>

            {submitSuccess && (
                <div style={{ padding: '16px', background: '#f0fdf4', color: '#166534', borderRadius: '12px', marginBottom: '24px', fontSize: '14px' }}>
                    Thank you! Your review has been submitted successfully.
                </div>
            )}

            {showForm && (
                <form onSubmit={handleSubmit} style={{ background: '#F9F9F9', padding: '24px', borderRadius: '16px', marginBottom: '32px' }}>
                    <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '16px' }}>Write Your Review</h3>
                    {submitError && (
                        <div style={{ padding: '12px', background: '#fef2f2', color: '#991b1b', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' }}>
                            {submitError}
                        </div>
                    )}
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>Rating</label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    type="button"
                                    onClick={() => setRating(star)}
                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#111' }}
                                >
                                    <Star size={24} fill={star <= rating ? '#111' : 'transparent'} strokeWidth={1.5} />
                                </button>
                            ))}
                        </div>
                    </div>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>Your Review</label>
                        <textarea 
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            required
                            rows="4"
                            style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #EAEAEA', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }}
                            placeholder="Share your experience with this product..."
                        />
                    </div>
                    <button 
                        type="submit" 
                        disabled={submitting}
                        style={{ 
                            padding: '14px 24px', 
                            background: '#111', 
                            color: '#fff', 
                            borderRadius: '12px', 
                            fontWeight: '600', 
                            border: 'none', 
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            opacity: submitting ? 0.7 : 1,
                            width: '100%',
                            fontSize: '15px'
                        }}
                    >
                        {submitting ? 'Submitting...' : 'Submit Review'}
                    </button>
                    <p style={{ marginTop: '12px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
                        Note: You must be logged in and have purchased this product to submit a review.
                    </p>
                </form>
            )}

            {!stats || stats.totalReviews === 0 ? (
                <div style={{ padding: '60px 20px', textAlign: 'center', background: '#F8F8F8', borderRadius: '24px' }}>
                    <Star size={48} color="#CCCCCC" strokeWidth={1} style={{ marginBottom: '16px' }} />
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#111' }}>No reviews yet</h3>
                    <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>Be the first to share your experience with this product.</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
                    {/* Stats Section */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '32px', background: '#F8F8F8', padding: '32px', borderRadius: '24px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ fontSize: '64px', fontWeight: '800', lineHeight: 1, color: '#111', marginBottom: '8px' }}>
                                {Number(stats.averageRating).toFixed(1)}
                            </div>
                            <div style={{ marginBottom: '8px' }}>
                                {renderStars(Math.round(stats.averageRating), 20)}
                            </div>
                            <div style={{ fontSize: '14px', color: '#666' }}>
                                Based on {stats.totalReviews} review{stats.totalReviews !== 1 ? 's' : ''}
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', justifyContent: 'center' }}>
                            {[5, 4, 3, 2, 1].map(star => {
                                const percentage = stats.distributionPercentages?.[star] || 0;
                                return (
                                    <div key={star} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '40px', fontSize: '13px', color: '#444' }}>
                                            {star} <Star size={12} fill="#666" color="#666" />
                                        </div>
                                        <div style={{ flex: 1, height: '8px', background: '#EAEAEA', borderRadius: '4px', overflow: 'hidden' }}>
                                            <div style={{ width: `${percentage * 100}%`, height: '100%', background: '#111', borderRadius: '4px' }} />
                                        </div>
                                        <div style={{ width: '40px', fontSize: '13px', color: '#666', textAlign: 'right' }}>
                                            {Math.round(percentage * 100)}%
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Reviews List */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        {reviews.map(review => (
                            <div key={review.id} style={{ paddingBottom: '24px', borderBottom: '1px solid #F0F0F0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                        <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#F0F0F0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                                            <User size={20} />
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: '600', fontSize: '15px', color: '#111', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                {review.userName || 'Anonymous'}
                                                {review.verified && (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', background: '#F8F8F8', color: '#111', border: '1px solid #111', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' }}>
                                                        <ShieldCheck size={12} /> Verified Buyer
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                                {renderStars(review.rating, 14)}
                                                <span style={{ fontSize: '13px', color: '#888', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <Calendar size={12} />
                                                    {new Date(review.createdAt).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <p style={{ margin: 0, fontSize: '15px', lineHeight: 1.6, color: '#333' }}>
                                    {review.comment}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
                            <button 
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                                disabled={page === 0}
                                style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #EAEAEA', background: '#fff', cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.5 : 1 }}
                            >
                                Previous
                            </button>
                            <span style={{ padding: '8px 16px', fontSize: '14px', display: 'flex', alignItems: 'center' }}>
                                Page {page + 1} of {totalPages}
                            </span>
                            <button 
                                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={page === totalPages - 1}
                                style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #EAEAEA', background: '#fff', cursor: page === totalPages - 1 ? 'not-allowed' : 'pointer', opacity: page === totalPages - 1 ? 0.5 : 1 }}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
};

export default ProductReviews;
